import { useMemo, useState } from 'react';
import { ExportToCsv, ExportToCsvProps } from '../ExportToCsv';
import { useQuery } from '@apollo/client';
import {
  CompanyCorporatePeriods,
  CompanyCorporatePeriodsVariables,
} from 'common/graphql/__generated__/CompanyCorporatePeriods';

import COMPANY_CORPORATE_PERIOD from 'common/graphql/CompanyCorporatePeriods.graphql';
import { CustomDropdown } from '../CustomDropdown';
import { Checkbox } from '@fluentui/react';
import { dateConvertions, dateFormat } from 'common/utils/dateFormats';
import { GraphQLFormattedError } from 'graphql';

export type AccountingExportCycleFn = (
  description: string | undefined,
  accountingPeriod: string | undefined,
  includeDistribution: boolean
) => Promise<{
  id?: string | null;
  errors?: readonly GraphQLFormattedError[];
}>;

type AccountingExportToCsvProps = Omit<ExportToCsvProps, 'getExportCycle'> & {
  getExportCycle: AccountingExportCycleFn;
};

export const AccountingExportToCsv: React.FC<AccountingExportToCsvProps> = ({
  onDialogOpen,
  getExportCycle,
  additionalComponents,
  ...props
}) => {
  const [includeAcctDist, setIncludeAcctDist] = useState<boolean>(false);
  const [accountingPeriod, setAccountingPeriod] = useState<string | null>(null);

  const { data: accoutringPeriods } = useQuery<
    CompanyCorporatePeriods,
    CompanyCorporatePeriodsVariables
  >(COMPANY_CORPORATE_PERIOD, {
    variables: {
      filter: {
        isEntryAllowed: {
          equalTo: true,
        },
      },
      orderBy: ['IS_ENTRY_ALLOWED_DESC', 'START_DATE_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const accountingPeriodOptions = useMemo(
    () =>
      accoutringPeriods?.companyCorporatePeriods?.nodes.map((item) => ({
        disabled: !item.isEntryAllowed,
        key: item.id,
        text: `${item._periodYear} (${dateFormat(
          dateConvertions(item.startDate)
        )} - ${dateFormat(dateConvertions(item.endDate))})`,
      })) || [],
    [accoutringPeriods]
  );

  return (
    <ExportToCsv
      onDialogOpen={() => {
        onDialogOpen?.();
        setIncludeAcctDist(false);
        setAccountingPeriod(null);
      }}
      additionalComponents={
        <>
          <CustomDropdown
            label="Accounting Period"
            placeholder="Select"
            selectedKey={accountingPeriod}
            options={accountingPeriodOptions}
            onChange={(_, option) => {
              if (option?.key) setAccountingPeriod(option.key as string);
            }}
            onClear={() => setAccountingPeriod(null)}
          />
          <Checkbox
            label="Include account distribution"
            onChange={(_, value) => setIncludeAcctDist(!!value)}
          />
          {additionalComponents}
        </>
      }
      getExportCycle={async (desc) =>
        await getExportCycle(
          desc,
          accountingPeriod || undefined,
          includeAcctDist
        )
      }
      {...props}
    />
  );
};
